import React, { useEffect, useState } from 'react'
import { useTheme, useMediaQuery } from '@material-ui/core';
import { Theme, Typography } from '@material-ui/core'
import { useSettings } from '../../contexts/SettingsProvider';
import { anime } from 'react-anime';
import { CardImage, resolveCardImage } from '../../themes/theme';

interface IPokerCardProps {
    value: string,
    hasEstimated: boolean,
    name: string
    invertFlip: boolean,
    image: CardImage | null
    isSelected: boolean,
    inGame: boolean,
    isAnonymous: boolean,
    isHorizontal: boolean,
    emote: string,
    isZoomed: boolean,
    isDisconnected?: boolean,
    onEmoteFinished: () => void
    selectKey: string
    onClick: (key: string) => void
}

export default function PokerCard({ value, hasEstimated, name, invertFlip = false, image = null, isSelected = false, inGame = false, 
        isAnonymous = false, isHorizontal = false, emote = "", isZoomed = false, isDisconnected = false, onEmoteFinished, selectKey, onClick }: IPokerCardProps) {
            
    const unselectedCssClass = (!hasEstimated ? " card-unselected" : "")
    const theme = useTheme<Theme>()
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'))
    const [prevEmote, setPrevEmote] = useState("")
    const { customCardColor, useCustomCardColor, cardImage, customCardImage, cardOutlineColor, useCustomCardOutline, cardOutlineThickness } = useSettings()

    const useCardImage = resolveCardImage(cardImage, customCardImage, image)
    const outlineColor = useCustomCardOutline ? cardOutlineColor : '#ffffff'
    const outlineWidth = typeof cardOutlineThickness === 'number' ? cardOutlineThickness : 2
    const dropShadow = '0 2px 8px 2px rgba(0, 0, 0, 0.3)'
    const faceShadow = outlineWidth > 0 ? `0 0 0 ${outlineWidth}px ${outlineColor}, ${dropShadow}` : dropShadow

    let cardContainerClassName = isHorizontal ? "card-container-horizontal" : "card-container"
    if (inGame) {
        cardContainerClassName += " card-container-ingame"
    }
    let cardClassName = isHorizontal ? "poker-card-horizontal" : "poker-card"

    function handleCardClicked() {
        if (onClick != null) {
            onClick(selectKey)
        }
    }

    // TODO - Try to make this dynamic
    function getCardValueFontSize(value: string): number {
        let length = value.length
        if (length < 5) {
            return 32
        } else if (length < 35) {
            return 24
        } else if (length < 60) {
            return 18
        } else if (length < 100) {
            return 16
        } else {
            return 12
        }
    }

    useEffect(() => {
        if (emote === "") {
            setPrevEmote(emote)
            return
        }
        if (emote !== prevEmote) {
            anime({
                targets: ".playerEmote" + selectKey,
                opacity: [1, 0],
                translateY: [0, -200],
                scale: [2, 4],
                easing: 'easeInOutSine',
                duration: 2500,
                complete: function () {
                    onEmoteFinished()
                }
            })
        }
    }, [emote])

    useEffect(() => {
        anime({
            targets: ".zoomable" + selectKey,
            scale: isZoomed ? (isNarrow ? 1.55 : 2.5) : 1,
            easing: 'easeInOutSine',
            duration: 300
        })
    }, [isZoomed])

    return (
        <div className={cardContainerClassName + " " + (isSelected ? "poker-card-selected" : "poker-card-unselected")} onClick={handleCardClicked} style={{
            borderRadius: 12,
            boxShadow: isSelected ? `0 0 0 2px ${theme.palette.primary.main}` : undefined,
        }}>
            <div className={cardClassName + " " + ((inGame ? "poker-card-game" : "")) + " zoomable" + selectKey}
                style={{
                    marginBottom: inGame ? "calc(8px * var(--player-card-scale, 1))" : "8px",
                    alignContent: "center",
                    justifyContent: "center",
                    zIndex: 10,
                    transformOrigin: "center center",
                    transform: invertFlip ? 'rotateY(180deg)' : undefined,
                    backfaceVisibility: invertFlip ? 'visible' : undefined,
                }}>
                <div className={"card-front" + unselectedCssClass} style={{
                    backgroundColor: useCustomCardColor ? customCardColor : theme.palette.primary.main,
                    backgroundImage: useCardImage != null ? `url("${useCardImage.url}")` : '',
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    boxShadow: faceShadow,
                    transform: isHorizontal ? "rotateZ(90deg)" : ""
                }} />
                <div className={"card-back" + unselectedCssClass}
                    style={{
                        transform: "rotateY(180deg)" + (isHorizontal ? " rotateZ(90deg)" : ""),
                        fontSize: `calc(${getCardValueFontSize(value ?? "")}px * var(--player-card-scale, 1))`,
                        color: useCustomCardColor ? customCardColor : theme.palette.primary.dark,
                        boxShadow: faceShadow,
                    }}>
                    <div style={{
                        transform: isHorizontal ? "rotateZ(90deg)" : "",
                        backfaceVisibility: "hidden"
                    }}>
                        {value != null ? value : ""}
                    </div>
                </div>
            </div>
            {!isAnonymous ? <Typography align="center" className="poker-card-name" style={{ transform: isHorizontal ? "translateY(-30px)" : undefined, textDecoration: isDisconnected ? 'line-through' : undefined, fontSize: "calc(18px * var(--player-card-scale, 1))", lineHeight: 1.15, margin: 0 }}> {name} </Typography> : <></>}
            <div className={"playerEmote" + selectKey} style={{ position: "absolute", zIndex: 15 }}>{emote}</div>

        </div>
    )
}